/**
 * Cookie Consent Hook
 * 
 * Manages cookie consent for non-authenticated users (localStorage-based)
 * Complies with EU ePrivacy Directive and Danish Cookie Law (Bekendtgørelse om cookies §3-4)
 * 
 * Different from useGDPR which manages authenticated user consents in Supabase
 */

import { useState, useEffect, useCallback } from 'react';

export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export interface CookieConsent {
  necessary: boolean;      // Always true, cannot be disabled
  functional: boolean;     // Optional - enhanced features
  analytics: boolean;      // Optional - usage statistics
  marketing: boolean;      // Optional - marketing and targeting
  timestamp: string;
  version: string;
}

const CONSENT_VERSION = '1.0';
const CONSENT_KEY = 'cookieConsent_v1';

const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: new Date().toISOString(),
  version: CONSENT_VERSION,
};

/**
 * Get stored consent from localStorage
 */
function getStoredConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    
    // Validate version
    if (parsed.version !== CONSENT_VERSION) {
      // Version mismatch, clear old consent
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
}

/**
 * Save consent to localStorage
 */
function saveConsent(consent: CookieConsent): void {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    
    // Trigger storage event for cross-tab sync
    window.dispatchEvent(new StorageEvent('storage', {
      key: CONSENT_KEY,
      newValue: JSON.stringify(consent),
      url: window.location.href,
      storageArea: localStorage,
    }));
  } catch (error) {
    console.error('Error saving cookie consent:', error);
  }
}

/**
 * Hook for managing cookie consent
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(() => getStoredConsent());
  const [showBanner, setShowBanner] = useState<boolean>(() => getStoredConsent() === null);
  const [loading, setLoading] = useState(false);

  /**
   * Grant consent for specific categories
   */
  const grantConsent = useCallback((categories: Partial<Omit<CookieConsent, 'timestamp' | 'version'>>) => {
    const newConsent: CookieConsent = {
      necessary: true, // Always true
      functional: categories.functional ?? false,
      analytics: categories.analytics ?? false,
      marketing: categories.marketing ?? false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    
    saveConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
    
    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('cookieConsentChange', { 
      detail: newConsent 
    }));
    
    return newConsent;
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = useCallback(() => {
    return grantConsent({
      functional: true,
      analytics: true,
      marketing: true,
    });
  }, [grantConsent]);

  /**
   * Reject all non-essential cookies
   */
  const rejectNonEssential = useCallback(() => {
    return grantConsent({
      functional: false,
      analytics: false,
      marketing: false,
    });
  }, [grantConsent]);

  /**
   * Check if a specific category is consented
   */
  const hasConsent = useCallback((category: CookieCategory): boolean => {
    if (category === 'necessary') return true; // Always allowed
    if (!consent) return false;
    return consent[category] === true;
  }, [consent]);

  /**
   * Withdraw consent (show banner again)
   */
  const withdrawConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(null);
    setShowBanner(true);
    
    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('cookieConsentChange', { 
      detail: null 
    }));
  }, []);

  /**
   * Reopen consent modal (without withdrawing)
   */
  const reopenBanner = useCallback(() => {
    setShowBanner(true);
  }, []);

  /**
   * Check if consent has been given
   */
  const hasGivenConsent = useCallback((): boolean => {
    return consent !== null;
  }, [consent]);

  // Listen for storage events (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONSENT_KEY) {
        if (e.newValue) {
          try {
            const newConsent = JSON.parse(e.newValue);
            setConsent(newConsent);
            setShowBanner(false);
          } catch (error) {
            console.error('Error parsing consent from storage event:', error);
          }
        } else {
          setConsent(null);
          setShowBanner(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    consent,
    showBanner,
    loading,
    grantConsent,
    acceptAll,
    rejectNonEssential,
    hasConsent,
    hasGivenConsent,
    withdrawConsent,
    reopenBanner,
  };
}

/**
 * Hook for conditional analytics initialization
 * Only initialize when consent is granted
 */
export function useConditionalAnalytics(callback: () => void) {
  const { hasConsent, consent } = useCookieConsent();
  
  useEffect(() => {
    if (hasConsent('analytics')) {
      callback();
    }
  }, [hasConsent, consent, callback]);
}

/**
 * Utility: Check if analytics should be loaded
 */
export function shouldLoadAnalytics(): boolean {
  const stored = getStoredConsent();
  return stored?.analytics === true;
}

/**
 * Utility: Check if marketing scripts should be loaded
 */
export function shouldLoadMarketing(): boolean {
  const stored = getStoredConsent();
  return stored?.marketing === true;
}





